import { IObjectTranslatable, RelationalActionTranslator, HtmlColorCodeTranslator, OptionSelectableItemHandler, TextAlignKindsTranslator } from ".";
import { IComboBoxItemBean, IComboBoxMenuItemBean, ITypeDefinable, BeanTypes } from "../beans/items";
import { IItem, ComboBoxItem, TextItemOption } from "../../../../domain/document/items";
import { RelationalActionExtractor } from "./RelationalActionExtractor";

import {
  Point,
  Size,
  Rectangle,
  Color,
  TextStyle,
} from "../../../../domain/drawing";

export class ComboBoxItemTranslator implements IObjectTranslatable {
  toItem(item: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const bean = item as IComboBoxItemBean;
    const result = new ComboBoxItem((bean.score !== null && bean.score !== undefined) ? bean.score.toString() : null, bean.enabledFreeText ? bean.text : !!bean.selectedIndex ? bean.selectedIndex : 0);
    result.privateId = (bean as any).privateId; // フロントエンド独自パラメータ.
    result.uniqueKey = bean.uniqueKey;
    result.id = bean.id;
    result.sectionObjectUid = bean.sectionObjectUid;
    result.isEditable = true; // TODO:特に問題なければプロパティを削除する
    result.isAdditional = !!bean.isAdditional;
    result.isDeleted = !!bean.isDeleted;
    result.isVisible = !!bean.isVisible;
    result.typeDetail = bean.typeDetail;
    result.viewTitle = bean.viewTitle;
    result.printBehavior.shouldPrint = !!bean.printVisible;
    result.printBehavior.shouldPreview = !!bean.previewVisible;
    result.name = bean.name;
    result.actions = RelationalActionTranslator.toItems(bean.relationalItems);
    result.shouldQuote = !!bean.isQuotable;
    result.supportedQuotable = !!bean.supportedQuotable;
    result.isFreeInputable = bean.supportedFreeText;
    result.shouldInputFree = bean.enabledFreeText;
    result.geometry.rectangle = new Rectangle(new Point(bean.x, bean.y), new Size(bean.width, bean.height));
    result.textStyle = new TextStyle(new Color(bean.textColor), TextAlignKindsTranslator.toKind(bean.textAlign), bean.textFontSize, bean.adjustedTextFontSize, bean.textFontName, bean.textLineHeight, false);
    result.label = bean.text;
    result.value = bean.text; // 基本的にはlabelと同じ値をセットしている（計算時はextractValueForCalculateで対応）
    result.code = bean.code;
    result.score = bean.score;
    result.options = this.toComboBoxMenuItems(bean.items, bean.selectedIndex);
    result.enableSingleEmptyCheck = bean.emptyCheck;
    result.enableEmptyCheckConfirmation = bean.isEmptyCheckIgnorable;
    result.enableNameGroupEmptyCheck = bean.nameGroupEmptyCheck;
    result.alertGroupKey = bean.alertGroup;
    result.toolTip = bean.toolTip;
    result.scriptExecuteParameters = RelationalActionExtractor.extractScriptExecuteParameters(result.actions);
    result.enableLengthCheck = !!bean.lengthCheck;
    result.lengthChecklength = bean.length;
    result.pasteDestinations = RelationalActionExtractor.extractTextPasteParameters(result.actions);
    result.parentAlertInformations = RelationalActionExtractor.extractParentAlertParameters(result.actions);
    result.enabledAutoAdjustFontSize = !!bean.enabledAutoAdjustFontSize;
    result.autoNotePageSize = bean.autoNotePageSize;
    result.autoNotePageKind = bean.autoNotePageType;
    result.paintStyle = HtmlColorCodeTranslator.toPaintStyleFill(bean.backColor);
    OptionSelectableItemHandler.applySelectedOptionValue(result);
    result.dacsProcessType = bean.dacsProcessType;
    result.dacsItemName = bean.dacsItemName;
    result.dacsItemCode = bean.dacsItemCode;
    result.isNaturalViewEnable = bean. naturalView;
    result.naturalText = bean.naturalText;
    return result;
  }

  toComboBoxMenuItems(items: IComboBoxMenuItemBean[], selectedIndex: number): TextItemOption[] {
    const results: TextItemOption[] = [];
    if (!items) {return results; }

    for (let index = 0; index < items.length; ++index) {
      const item = items[index];
      const isSelected = (index === selectedIndex);
      results.push(this.toComboBoxMenuItem(item, isSelected));
    }
    return results;
  }

  toComboBoxMenuItem(item: IComboBoxMenuItemBean, isSelected: boolean): TextItemOption {
    return new TextItemOption(
      item.name,
      item.text,
      new Color(item.foreColor),
      new Color(item.backColor),
      isSelected,
      item.score,
      item.code,
    );
  }

  toBean(item: IItem): ITypeDefinable {
    const target = item as ComboBoxItem;
    const bean: IComboBoxItemBean = {
      type: BeanTypes.List,
      uniqueKey: target.uniqueKey,
      id: target.id,
      sectionObjectUid: target.sectionObjectUid,
      name: target.name,
      isVisible: target.isVisible,
      printVisible: target.printBehavior.shouldPrint,
      previewVisible: target.printBehavior.shouldPreview,
      x: target.geometry.rectangle.x,
      y: target.geometry.rectangle.y,
      width: target.geometry.rectangle.width,
      height: target.geometry.rectangle.height,
      isAdditional: target.isAdditional,
      isDeleted: target.isDeleted,
      textColor: HtmlColorCodeTranslator.fromColor(target.textStyle.color),
      textFontName: target.textStyle.fontFamily,
      textFontSize: target.textStyle.baseFontSize,
      textLineHeight: target.textStyle.lineHeight,
      textAlign: TextAlignKindsTranslator.fromKind(target.textStyle.textAlign),
      backColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyle),
      // ComboBoxは編集色がなく印刷色と連動する？(v1動作)とはいえdisplayBackColorはフロントエンドしか見ないので問題なし.
      displayBackColor: null,
      text: target.label,
      code: target.code,
      score: target.score,
      items: this.toComboBoxMenuItemBeans(target.options),
      relationalItems: null,  // TODO:返却する必要ある？
      supportedFreeText: target.isFreeInputable,
      enabledFreeText: target.shouldInputFree,
      emptyCheck: target.enableSingleEmptyCheck,
      isEmptyCheckIgnorable: target.enableEmptyCheckConfirmation,
      nameGroupEmptyCheck: target.enableNameGroupEmptyCheck,
      alertGroup: target.alertGroupKey,
      toolTip: target.toolTip,
      isQuotable: target.shouldQuote,
      supportedQuotable: target.supportedQuotable,
      lengthCheck: target.enableLengthCheck,
      length: target.lengthChecklength,
      selectedIndex: target.shouldInputFree ? null : OptionSelectableItemHandler.getSelectedIndex(target.options),
      enabledAutoAdjustFontSize: target.enabledAutoAdjustFontSize,
      autoNotePageSize: target.autoNotePageSize,
      autoNotePageType: target.autoNotePageKind,
      adjustedTextFontSize: target.textStyle.adjustedFontSize,
      isDisplayBackColorSpecified: false,
      typeDetail: target.typeDetail,
      viewTitle: target.viewTitle,
      dacsProcessType: target.dacsProcessType,
      dacsItemName: target.dacsItemName,
      dacsItemCode: target.dacsItemCode,
      naturalView: target.isNaturalViewEnable,
      naturalText: target.naturalText,
    };
    return bean;
  }

  toComboBoxMenuItemBeans(items: TextItemOption[]): IComboBoxMenuItemBean[] {
    const beans: IComboBoxMenuItemBean[] = [];
    for (const item of items) {
      beans.push(this.toComboBoxMenuItemBean(item));
    }
    return beans;
  }

  toComboBoxMenuItemBean(item: TextItemOption): IComboBoxMenuItemBean {
    return {
      name: item.name,
      text: item.text,
      score: item.score,
      code: item.code,
      foreColor: HtmlColorCodeTranslator.fromColor(item.textColor),
      backColor: HtmlColorCodeTranslator.fromColor(item.backgroundColor),
    };
  }
}
